'use client';

import { c as _c } from "react/compiler-runtime";
import type { AuthorProfile } from '@svs/nova-poolbetting/schema/content';
import type { Definition } from '@svs/nova-poolbetting/schema/frameworks';
import * as TipsenSheet from '@svs/nova-poolbetting/ui/TipsenSheet';
import Information from '@svs/ui/react/icons/Information';
import { RadioGroup, RadioGroupItem } from '@svs/ui/react/radio-group';
import { showSnackbar, Snackbar } from '@svs/ui/react/snackbar';
import { ToggleGroup, ToggleGroupItem } from '@svs/ui/react/toggle-group';
import { type ReactNode, type SubmitEvent, useEffect, useMemo } from 'react';
type QuickPickFormProps = {
  quickPickSelections: Definition['quickPickMsys'] | Definition['quickPickSrow'];
  handleSubmit: (event: SubmitEvent<HTMLFormElement>) => Promise<void>;
  isPending: boolean;
  isError: boolean;
  quickPickSize: number;
  setQuickPickSize: (size: number) => void;
  quickPickBase: 'ownChoice' | 'emptyCoupon' | `analysis-${string}`;
  setQuickPickBase: (value: 'ownChoice' | 'emptyCoupon' | `analysis-${string}`) => void;
  authorProfiles: AuthorProfile[];
  isValueDisabled: (value: number) => boolean;
  isSingleRow: boolean;
  shouldShowRadioGroup: boolean;
};

/**
 * Quick pick form component.
 *
 * @param root0 component props
 * @param root0.quickPickSelections Quick pick selections values to render
 * @param root0.handleSubmit Form submit handler
 * @param root0.isPending Indicates if the form submission is pending
 * @param root0.isError Indicates if there was an error during form submission
 * @param root0.quickPickSize Currently selected quick pick size
 * @param root0.setQuickPickSize Function to update the selected quick pick size
 * @param root0.quickPickBase Currently selected value for the quick pick base
 * @param root0.setQuickPickBase Function to update the selected value for the quick pick base
 * @param root0.authorProfiles Game analysis data for authors
 * @param root0.isValueDisabled Function to determine if a quick pick value is disabled
 * @param root0.isSingleRow Indicates if the quick pick is for a single row coupon
 * @param root0.shouldShowRadioGroup Indicates if the radio group for quick pick base selection should be shown
 * @returns Quick pick form component
 * @throws {Error} Will throw an error if draw number is not available
 */
export default function QuickPickForm(t0) {
  const $ = _c(43);
  const {
    quickPickSelections,
    handleSubmit,
    isPending,
    isError,
    quickPickSize,
    setQuickPickSize,
    quickPickBase,
    setQuickPickBase,
    authorProfiles,
    isValueDisabled,
    isSingleRow,
    shouldShowRadioGroup
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="flex items-center gap-2"><span className="text-base"><Information /></span><span>Hoppsan! Något gick fel, försök igen.</span></div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const snackbarText = t1;
  let t2;
  let t3;
  if ($[1] !== isError) {
    t2 = () => {
      if (isError) {
        showSnackbar({
          text: snackbarText,
          variant: "important",
          showCloseButton: true
        });
      }
    };
    t3 = [isError, snackbarText];
    $[1] = isError;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  if ($[4] !== handleSubmit) {
    t4 = event => {
      handleSubmit(event);
    };
    $[4] = handleSubmit;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = isSingleRow ? "V\xE4lj antal rader" : "V\xE4lj belopp";
  let t6;
  if ($[6] !== t5) {
    t6 = <legend className="pb-4 font-medium">{t5}</legend>;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const t7 = isValueDisabled(quickPickSize) ? "" : `${quickPickSize}`;
  let t8;
  if ($[8] !== setQuickPickSize) {
    t8 = value => {
      if (value === "") {
        return;
      }
      setQuickPickSize(Number(value));
    };
    $[8] = setQuickPickSize;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== isSingleRow || $[11] !== isValueDisabled || $[12] !== quickPickSelections) {
    t9 = quickPickSelections?.map(msys => {
      let quickPickValue = `${msys} kr`;
      if (isSingleRow) {
        quickPickValue = msys === 1 ? `${msys} rad` : `${msys} rader`;
      }
      return <ToggleGroupItem className="border-stroke-medium h-10 w-full rounded-none border border-r-0 border-b-0 whitespace-nowrap first:rounded-l-md data-[state=on]:border nth-[-n+3]:border-t-0 data-[state=on]:nth-[-n+3]:border-t nth-[3n+1]:border-l-0 data-[state=on]:nth-[3n+1]:border-l" key={msys} value={`${msys}`} disabled={isValueDisabled(msys)}>{quickPickValue}</ToggleGroupItem>;
    });
    $[10] = isSingleRow;
    $[11] = isValueDisabled;
    $[12] = quickPickSelections;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t7 || $[15] !== t8 || $[16] !== t9) {
    t10 = <ToggleGroup className="border-stroke-medium grid w-full grid-cols-3 gap-0 rounded-none border *:first:rounded-none *:last:rounded-none" type="single" context="base" emphasis="low" value={t7} onValueChange={t8}>{t9}</ToggleGroup>;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t6) {
    t11 = <fieldset>{t6}{t10}</fieldset>;
    $[18] = t10;
    $[19] = t6;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== authorProfiles || $[22] !== quickPickBase || $[23] !== setQuickPickBase || $[24] !== shouldShowRadioGroup) {
    t12 = shouldShowRadioGroup && <fieldset><legend className="pt-6 pb-4 font-medium">Utgå ifrån</legend><RadioGroup value={quickPickBase} onValueChange={value_0 => {
        setQuickPickBase(value_0);
      }} orientation="vertical"><div className="grid auto-cols-max grid-flow-col items-center gap-2"><RadioGroupItem aria-label="Egna val" id="ownChoice" value="ownChoice" /><label htmlFor="ownChoice">Egna val</label></div><div className="grid auto-cols-max grid-flow-col items-center gap-2"><RadioGroupItem aria-label="Tom kupong" id="emptyCoupon" value="emptyCoupon" /><label htmlFor="emptyCoupon">Tom kupong</label></div>{authorProfiles.length > 0 && authorProfiles.map(_temp)}</RadioGroup></fieldset>;
    $[21] = authorProfiles;
    $[22] = quickPickBase;
    $[23] = setQuickPickBase;
    $[24] = shouldShowRadioGroup;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Snackbar />;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t11 || $[28] !== t12) {
    t14 = <TipsenSheet.Content>{t11}{t12}{t13}</TipsenSheet.Content>;
    $[27] = t11;
    $[28] = t12;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== isError || $[31] !== isPending || $[32] !== isValueDisabled || $[33] !== quickPickSize) {
    t15 = isPending || isError || isValueDisabled(quickPickSize);
    $[30] = isError;
    $[31] = isPending;
    $[32] = isValueDisabled;
    $[33] = quickPickSize;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t15) {
    t16 = <TipsenSheet.Footer><TipsenSheet.Close type="submit" disabled={t15}>Generera PIX!</TipsenSheet.Close></TipsenSheet.Footer>;
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== t14 || $[38] !== t16) {
    t17 = <div className="grid h-full grid-rows-[auto_1fr_auto]">{t14}{t16}</div>;
    $[37] = t14;
    $[38] = t16;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== t17 || $[41] !== t4) {
    t18 = <form onSubmit={t4} className="h-full">{t17}</form>;
    $[40] = t17;
    $[41] = t4;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  return t18;
}
function _temp(authorProfile) {
  return <div className="grid auto-cols-max grid-flow-col items-center gap-2" key={authorProfile.id}><RadioGroupItem aria-label={`${authorProfile.longName}s analys`} id={`analysis-${authorProfile.id}`} value={`analysis-${authorProfile.id}`} /><label htmlFor={`analysis-${authorProfile.id}`}>{`${authorProfile.longName}s analys`}</label></div>;
}